import * as Yup from 'yup';

export const LoginSchema = Yup.object().shape({
  username: Yup.string()
    .trim()
    .required()
    .min(5)
    .max(64),
  password: Yup.string()
    .trim()
    .required()
    .min(8)
    .max(64),
});

export const ForgotSchema = Yup.object().shape({
  email: Yup.string()
    .email('Invalid email format')
    .required('Email is required')
});

export const ResetSchema = Yup.object().shape({
  password: Yup.string()
    .trim()
    .required('Password is required')
    .min(8, 'Password must be at least 8 characters')
    .max(64, 'Password must be less then 64 characters'),
  passwordConfirmation: Yup.string()
    .trim()
    .required('Confirm Password is required')
    .min(8, 'Confirm Password must be at least 8 characters')
    .max(64, 'Confirm Password must be less then 64 characters')
    .oneOf([Yup.ref('password')], 'Passwords must match'),
});

export const SetPasswordSchema = Yup.object().shape({
  password1: Yup.string()
    .trim()
    .required()
    .min(8)
    .max(64),
  password2: Yup.string()
    .trim()
    .required()
    .min(8)
    .max(64)
    .oneOf([Yup.ref('password1')], 'Passwords must match'),
});

export const AccountSettingsSchema = Yup.object().shape({
  username: Yup.string()
    .trim()
    .required()
    .min(5)
    .max(64),
  phone: Yup.string()
    .default('')
    .trim(),
  firstName: Yup.string()
    .default('')
    .trim(),
  lastName: Yup.string()
    .default('')
    .trim(),
  // odometerUnits: Yup.string()
  //   .default('')
  //   .trim()
});

export const UserSettingsSchema = Yup.object().shape({
  username: Yup.string()
    .trim()
    .required()
    .min(5)
    .max(64),
  // .matches(/^[a-zA-Z0-9]+$/, 'String or Digits'),
  email: Yup.string()
    .email('Invalid email format')
    .required('Email is required'),
  password1: Yup.string()
    .trim()
    .min(8)
    .max(64),
  password2: Yup.string()
    .trim()
    .min(8)
    .max(64)
    .oneOf([Yup.ref('password1')], 'Passwords must match'),
  firstName: Yup.string()
    .default('')
    .min(2)
    .max(30)
    .trim(),
  lastName: Yup.string()
    .default('')
    .min(2)
    .max(30)
    .trim(),
  isActive: Yup.boolean().default(true)
});

export const DriverSettingsSchema = Yup.object().shape({
  companyDriverId: Yup.string().default(''),
  company: Yup.string().nullable().default(null),
  phone: Yup.string()
    .default('')
    .required()
    .trim(),
  group: Yup.string().default(''),
  driverLicenseCountry: Yup.string()
    .default('')
    .required()
    .trim(),
  driverLicenseState: Yup.string()
    .default('')
    .required()
    .trim(),
  driverLicenseNumber: Yup.string()
    .default('')
    .required()
    .trim(),
  homeTerminal: Yup.number()
    .required(),
  assignedVehicles: Yup.array().default([]),
  assignedVehicle: Yup.number().nullable(),
  hosRules: Yup.string()
    .required()
    .trim(),
  cargoType: Yup.string()
    .required()
    .trim(),
  restart: Yup.string()
    .required()
    .trim(),
  restBreak: Yup.string()
    .required()
    .trim(),
  exemptDriver: Yup.boolean().default(false),
  shortHaulException: Yup.boolean().default(false),
  allowPersonalUse: Yup.boolean().default(false),
  allowYardMoves: Yup.boolean().default(false),
  unlimitedTrailers: Yup.boolean().default(false),
  unlimitedShippingDocuments: Yup.boolean().default(false),
});

export const VehicleSettingsSchema = Yup.object().shape({
  make: Yup.string().trim().required(),
  model: Yup.string().trim().required(),
  year: Yup.string().trim().required(),
  vin: Yup.string().length(17).trim().required(),
  fuel: Yup.string().trim().required(),
  eldConnectionInterface: Yup.string().trim().required(),
  licensePlateCountry: Yup.string().trim().required(),
  licensePlateState: Yup.string().trim().required(),
  licensePlateNumber: Yup.string()
    .trim()
    .required('License plate is required')
    .test(
      'plate-validation',
      'License plate number already exists',
      function (value) {
        const { vehicles, currentVehicleId } = this.options.context as any;
        if (!vehicles?.results) return true;
        if (!value) return true;

        const normalizedValue = value.trim().toUpperCase();
        const exists = vehicles.results.some((v: any) => {
          const isSameNumber = v.licensePlateNumber?.trim().toUpperCase() === normalizedValue;
          const isNotCurrent = String(v.id) !== String(currentVehicleId);

          return isSameNumber && isNotCurrent;
        });

        return !exists;
      },
    ),
});

export const CompanyGeneralSchema = Yup.object().shape({
  name: Yup.string()
    .trim()
    .required()
    .min(3),
  dotNumber: Yup.string()
    .trim()
    .required()
    .min(5),
  mcNumber: Yup.string()
    .trim()
    .min(7)
    .max(7)
    .default(''),
  timezone: Yup.string()
    .trim()
    .required(),
  country: Yup.string()
    .trim()
    .required(),
  street: Yup.string()
    .trim()
    .required(),
  city: Yup.string()
    .trim()
    .required(),
  state: Yup.string()
    .trim()
    .required(),
  zip: Yup.string()
    .trim()
    .required(),
  phone: Yup.string()
    .trim()
    .default(''),
  contactPhone: Yup.string()
    .trim()
    .default(''),
  email: Yup.string()
    .email('Invalid email format')
    .trim()
    .default(''),
});

export const CompanySettingsSchema = Yup.object().shape({
  hosRules: Yup.string()
    .required()
    .trim(),
  cargoType: Yup.string()
    .required()
    .trim(),
  restart: Yup.string()
    .required()
    .trim(),
  restBreak: Yup.string()
    .required()
    .trim(),
  exemptDriver: Yup.boolean().default(false),
  shortHaulException: Yup.boolean().default(false),
  allowPersonalUse: Yup.boolean().default(false),
  allowYardMoves: Yup.boolean().default(false),
});

export const GridInfoTerminalItemSchema = Yup.object().shape({
  timezone: Yup.string()
    .required()
    .trim(),
  street: Yup.string()
    .required()
    .trim(),
  city: Yup.string()
    .required()
    .trim(),
  country: Yup.string()
    .required()
    .trim(),
  state: Yup.string()
    .required()
    .trim(),
  zip: Yup.string()
    .required()
    .trim(),
});

export const EditLogSchema = Yup.object().shape({
  timezone: Yup.string()
    .notRequired()
    .trim(),
  datetime: Yup.string()
    .required()
    .trim(),
  event: Yup.string()
    .required()
    .trim(),
  vehicle: Yup.number().notRequired(),
  driverLocationDescription: Yup.string()
    .notRequired()
    .trim(),
  location: Yup.string()
    .notRequired(),
  notes: Yup.string()
    .notRequired()
    .trim(),
});

export const ParamsSendReport = Yup.object().shape({
  driverId: Yup.number()
    .required(),
  type: Yup.string()
    .required()
    .trim(),
  start: Yup.string()
    .required()
    .trim(),
  end: Yup.string()
    .required()
    .trim(),
  comment: Yup.string()
    .notRequired()
    .trim(),
  email: Yup.string()
    .email('Invalid email format')
    .notRequired(),
});

export const ParamsDownloadReport = Yup.object().shape({
  driverId: Yup.number()
    .required(),
  start: Yup.string()
    .required()
    .trim(),
  end: Yup.string()
    .required()
    .trim(),
  format: Yup.string()
    .required()
    .trim(),
});

export const ParamsGenerateReport = Yup.object().shape({
  vehicleIds: Yup.array()
    .required(),
  start: Yup.string()
    .required()
    .trim(),
  end: Yup.string()
    .required()
    .trim(),
});